import {priority} from '@priority-software/priority-proto-api/lib/nform';
import {addAndApplyFilters} from './filters';
import {zoomFetch} from './zoom';
import {setFormSystemConstants, openForm, closeFormBySession} from './form';
import {MessageHandlers} from '../../../services/proto/core/server';
import {handleMessage} from '../../../features/Messages/actions';
import {formsStore, APIActionStatus, Screen, apiLocker} from '../../../stores/Form';
import {Form} from '../../../stores/Form/Form';
import {addNewEntity} from '../../../_shared/GWTBridge/invoke/genPage';
import {SuspendedJobTypes} from '../consts/suspendedJobTypes';
import {isDevEnvProcess} from '../../../utils/platform';
import {closeCurrentAndMinimizeAll} from '../../../_shared/GWTBridge/invoke/genPage';
/**
 * handleScreenMessage
 * response - the response from API call;
 * handlers - Callbacks to add an extra logic while handling response Messages;
 * continueCallback - function that should be call after successful message check;
 * i.e. If there is an empty Messages array or the user allows to proceed the action (confirm warning);
 **/

export const handleScreenMessage = async (
    response,
    handlers: MessageHandlers = {},
    continueCallback?: (res?: any) => void,
    suppressErrors: boolean = false,
    activeSession: string = null
) => {
    const currentScreen = formsStore.currentScreen;
    const session = activeSession || currentScreen?.session;

    const messageHandlers = {
        ...handlers,
        onClose: () => {
            handlers?.onClose?.();
            const formActiveElement = currentScreen?.getActiveForm()?.formActiveElement;

            if (formActiveElement) {
                formActiveElement.focus();

                if (formActiveElement.tagName.toLowerCase() === 'input') {
                    (formActiveElement as HTMLInputElement).select();
                }
            }
            isDevEnvProcess() && console.log('close screen message handler');
        },
        onErrorAccept: async () => {
            await handlers?.onErrorAccept?.();
            if (!session) closeCurrentAndMinimizeAll();

            isDevEnvProcess() && console.log('default screen error accept handler');
        },
        onWarningConfirm: async (res: priority.netitems.form.WarningApproveRs) => {
            handlers?.onWarningConfirm?.(res);
            if (res.Record) {
                const {currentRow, updateRecordValues} = currentScreen?.getActiveForm() || {};
                if (updateRecordValues) {
                    updateRecordValues(currentRow, res.Record.Fields);
                }
            }
            if (res.Messages.length === 0) {
                await continueCallback?.(res);
                isDevEnvProcess() && console.log('default onWarningConfirm handler');
            }
        }
    };

    if (continueCallback && !response?.Messages?.length) {
        await continueCallback();
    }

    const isAllowed = await handleMessage(response, session, messageHandlers, suppressErrors);

    // message can provide the field id to be focused
    // if it's provided, set it as the currentColumnId
    const nextFieldId = response?.Messages?.[0]?.FieldId;
    if (nextFieldId) {
        currentScreen?.getActiveForm().setCurrentColumnId(nextFieldId);
    }

    return isAllowed;
};

/*
 * Action that allows execution of 2 chained actions / methods. Each action should return `APIActionStatus`;
 * It's helpful when you need to stop execution of the next action by some circumstances.
 */

export const callLinkedActions = async function (
    ...actions: Array<() => Promise<APIActionStatus>>
) {
    const nextActionAllowed = await actions[0]?.();
    if (nextActionAllowed) {
        const nextActions = actions.slice(1, actions.length);
        if (nextActions.length === 0) return APIActionStatus.passed;
        return await callLinkedActions(...nextActions);
    }
    return APIActionStatus.blocked;
};

export const startNewForm = async (
    formName: string,
    zoomValue: string = null,
    queryParams: [{fieldName: string; value: string}] = null
) => {
    await setFormSystemConstants();

    const shouldRunDefaultSearch = !queryParams;
    const newForm: Form | null = zoomValue
        ? await zoomFetch(formName, zoomValue)
        : await openForm(formName, shouldRunDefaultSearch);

    if (newForm) {
        if (queryParams) {
            const filterParams = [];
            queryParams.forEach(param => {
                const fieldId = newForm.getColumnByName(param.fieldName)?.id;
                const value = param.value;
                filterParams.push({fieldId, value});
            });
            addAndApplyFilters(newForm, filterParams);
        }
        addNewScreen(newForm);

        addNewEntity(newForm.session, newForm.ename, newForm.title);
    }
};

export const addNewScreen = (form: Form, source: Screen = null, standAlone = false) => {
    // If form in unprepared we get no session back from the server
    if (!form?.session) return;

    const newScreen = new Screen(form, source, standAlone);
    if (source) {
        source.setZoomChild(newScreen);
    }
    formsStore.addForm(form.session, newScreen);
    formsStore.setNewSession(form.session);
};

export const openNewScreen = async (eName: string, linkScreen: Screen) => {
    const form = await openForm(eName);
    addNewScreen(form, linkScreen);
};

export const unlockSession = (session: string) => {
    apiLocker.unlockSession(session);
};

export const moveUp = async (level: number, session: string) => {
    const currentScreen = formsStore.currentScreen;
    if (
        !currentScreen ||
        (currentScreen.session === session && currentScreen.activeLevel === level)
    ) {
        return;
    }
    if (currentScreen.session === session || currentScreen.activeLevel > 0) {
        await currentScreen?.handleFormLevel(currentScreen.activeLevel - 1);
        formsStore.setEventForRecordingMacro({
            type: SuspendedJobTypes.ZoomFormExit,
            formLevel: currentScreen.activeLevel - 1
        });
    } else {
        await closeFormBySession(currentScreen.session, false);
        formsStore.setEventForRecordingMacro({type: SuspendedJobTypes.ZoomFormExit});
    }
    moveUp(level, session);
};

export const subformZoom = async (fromName: string, subFormName: string, zoomValue: string) => {
    await startNewForm(fromName, zoomValue);
    const mainForm = formsStore.getActiveForm();
    const screen = formsStore.currentScreen;
    return screen && screen.runSubForm(subFormName, mainForm, true);
};
